import { getCatalogReference } from '@/mocks/gobeletcupCatalog';
import {
    estimateLeadTimeInWorkingDays,
    estimateUnitPriceExclVatInEur,
    EXPRESS_LEAD_TIME_IN_WORKING_DAYS,
    toInclVat,
    VISUAL_CHECK_PRICE_INCL_VAT_IN_EUR,
} from '@/mocks/gobeletcupPricing';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { TInkTreatment } from '@/domain/print/TInkTreatment';
import type { TPatternArea } from '@/domain/print/TPatternArea';
import type { TSide } from '@/domain/shared/TSide';
import type { ICatalogReference, TCupColour, TQuadriPlacement } from '@/mocks/gobeletcupCatalog';
import type { TPrintTechnique } from '@/mocks/gobeletcupPricing';

// The customer's choices, all made from the editor's configuration rail: cup model and
// colour, print style and zone, how they want to create (from scratch or from a ready-made
// design), quantity. Lives in memory for the session; a reload on the editor route falls
// back to the model in the URL.
// Prototype state, CDC domains CFG, RES and CMD.

type TRailSection = 'mode' | 'model' | 'style' | 'template';

// Scratch: the customer composes with text and images. Template: they start from one of
// the client's ready-made designs, browsed by theme.
type TCreationMode = 'scratch' | 'template';

const DEFAULT_CARTONS = 4;

// The two screen-printing styles, the only ones that reduce the marking to a count of inks.
type TPrintedFaces = 'back' | 'both' | 'front';

type TBackgroundPatternChoice = { area?: TPatternArea; colour: string; patternId: string; sizeInMm: number };

type TNominativeFile = { fileName: string; records: string[] };

// A change the customer asked for that costs them something once they are working, waiting for
// their answer (useConfigurationChange): a cup, a style, the faces of a pitcher, the front taken
// over a back that already carries something.
type TPendingChange =
    | { kind: 'copyFront' }
    | { kind: 'faces'; value: TPrintedFaces }
    | { kind: 'model'; value: string }
    | { kind: 'style'; value: TPrintTechnique };

const SCREEN_PRINTED: TPrintTechnique[] = ['serigraphy1', 'serigraphy2'];
// The styles printed digitally, the ones a personalisation by name can go on: both quadri heights
// (client's review, 2026-10-12, Katia's go; the CDC's FIC-02 names the short one only).
const BY_NAME_TECHNIQUES: TPrintTechnique[] = ['quadriShort', 'quadriTall'];
// What a press is loaded with when the customer has not said otherwise: black, and a second
// colour that is visibly a second colour so bicolore reads as bicolore from the first glance.
const DEFAULT_INKS = ['#1F1F1F', '#2CAFA5'];

// The frosted translucent cup is the one GobeletCup keeps in stock and sells most, so it is
// what a reference opens on when it offers it (client, 2026-09-22). Falling back to the first
// colour matters: a few references have no frosted variant at all.
const PREFERRED_CUP_COLOUR: TCupColour = 'ice';

function defaultColourOf(reference: ICatalogReference): TCupColour {
    return reference.colours.includes(PREFERRED_CUP_COLOUR) ? PREFERRED_CUP_COLOUR : reference.colours[0];
}

function availableTechniques(reference: ICatalogReference | undefined): TPrintTechnique[] {
    if (!reference) {
        return [];
    }
    const techniques: TPrintTechnique[] = ['serigraphy1'];
    if (reference.serigraphy.maxColours === 2) {
        techniques.push('serigraphy2');
    }
    if (reference.quadriShort) {
        techniques.push('quadriShort');
    }
    if (reference.quadriTall) {
        techniques.push('quadriTall');
    }
    return techniques;
}

function availablePlacements(reference: ICatalogReference | undefined, technique: TPrintTechnique): TQuadriPlacement[] {
    if (!reference?.quadriShort || technique !== 'quadriShort') {
        return [];
    }
    return Object.keys(reference.quadriShort.placements) as TQuadriPlacement[];
}

const useOrderStore = defineStore('order', () => {
    const referenceName = ref<string>();
    // On the phone steps screen the canvas is not on screen yet, so a change of cup waits
    // here instead of re-initialising the editor under the customer's fingers: they compare
    // cups and styles without the screen blanking out. Played when they reach the
    // personalisation step or leave for the canvas. See useConfigurationActions.
    const pendingReferenceName = ref<string>();
    // Here rather than in the shell, so the rail can propose a change the shell asks about.
    const pendingChange = ref<TPendingChange>();
    const cupColour = ref<TCupColour>();
    const technique = ref<TPrintTechnique>('serigraphy1');
    const quadriPlacement = ref<TQuadriPlacement>('centre');
    const creationMode = ref<TCreationMode>('scratch');
    const quantity = ref(0);
    const templateCategoryId = ref<string>();
    const templateSubCategory = ref<string>();
    // Step 3 on the design path browses in three levels, themes, then a theme's
    // sub-categories, then designs, and the theme and sub-category kept here are what lets the
    // cup screen's sheet reopen where the customer left off. This is the third level reached
    // through "Voir tous les modèles" rather than through a sub-category.
    const isWholeThemeBrowsed = ref(false);
    // The ready-made design in use, from the prototype's own library (the backend carries no
    // design for the cup references): what the gallery shows as chosen. Applying it to the
    // canvas needs the client's library, see PROTO-NOTES.
    const appliedTemplateModelId = ref<string>();
    // The graduation design in use, from the prototype's own library: the socle knows one
    // graduation image per cup size, the style is what the customer picked in the selector.
    const graduationStyleId = ref<string>();
    // The client asks for the graduation's colour to be the customer's, "afin de l'adapter à
    // sa personnalisation" — which is why they supply it as SVG.
    const graduationColour = ref('#000000');
    // The colour the next shape is laid in, chosen in the shapes panel before it is added; the
    // shape bar changes it afterwards, per shape.
    const shapeColour = ref('#000000');
    // The repeated motif behind each face (EDI-09), as the customer set it: which one, how big a
    // repeat is, in what colour. A rendering instruction like the colours below, not serialised;
    // the scene is told through useBackgroundPattern.
    const backgroundPatterns = ref<Partial<Record<TSide, TBackgroundPatternChoice>>>({});
    // The personalisation by name (FIC-02): the file the customer imported, one line per cup, and
    // the text fields of the design that stand for its first column. A first name is the usual
    // line, the 3D view and the BAT show the first of them. The import is simulated.
    const nominative = ref<TNominativeFile>();
    const nominativeFieldIds = ref<string[]>([]);
    // Ticking the personalisation by name is what makes it an option of the order, priced per
    // cup, before any file is brought (client's review, 2026-10-12: it changes the price, so it
    // is a box to tick with a note saying so).
    const isNominativeChosen = ref(false);
    // The paid options ticked in the recap before the cart (client's review, 2026-10-12): the
    // check of the visual by the designers, and the express production.
    const hasVisualCheck = ref(false);
    const isExpress = ref(false);
    // Screen printing lays one or two inks and nothing in between, so the marking is painted
    // with these rather than with the colours each element carries. The threshold is where the
    // split falls on a photograph, the client's answer to "how do we turn an image into two
    // colours", taken at the kickoff in place of any Pantone matching. Both are the customer's:
    // the inks are picked in the 3D scene, the threshold when they import an image.
    const inkColours = ref<string[]>([...DEFAULT_INKS]);
    const inkThreshold = ref(0.5);
    // How much of the white of an imported file is left unprinted, so the cup shows through.
    // Generous by default, because the hardened contrast has already pushed every pixel to one
    // end or the other. One ink only: with two, nothing is left bare.
    const whiteDistance = ref(0.2);

    // The rail shows one section at a time; the top bar can point it at the model.
    const openSection = ref<TRailSection | undefined>('model');
    // Step 2 asks how the customer wants to create, from scratch or from a ready-made design,
    // before any style or design is shown (client's review, 2026-09-22). `creationMode` opens
    // on `scratch` so that a price can be read from the very first screen, which is not the
    // same thing as the customer having answered: this says whether they have.
    const isCreationModeChosen = ref(false);
    // Step 3 is the customer's own answer to how the cup is printed, a style on one path or a
    // design on the other. The store opens on a technique for the same pricing reason, and
    // this says whether the customer has chosen. Nothing leaves the steps screen before.
    const isStyleChosen = ref(false);
    // Mobile: the steps screen shows first and only "Personnaliser" leads to the canvas;
    // both flags live here so a change of cup or design, which re-initialises the editor,
    // brings the customer back where they were.
    const isMobileStepsDone = ref(false);
    const isMobileStepsOpen = ref(true);
    // Mobile again: once the steps are done the cup in 3D is the home screen, and the 2D
    // scene rises over it as a layer. Here so a change of cup or design brings the customer
    // back to the same place.
    const isMobileEditorOpen = ref(false);

    const reference = computed(() => (referenceName.value ? getCatalogReference(referenceName.value) : undefined));
    const techniques = computed(() => availableTechniques(reference.value));
    const placements = computed(() => availablePlacements(reference.value, technique.value));

    // The pitchers are printed on the front, on the back, or on both (two prices on the site: one
    // face, two faces). Everything else is front only, whatever this says. The front is the
    // recto and the back the verso, so each face keeps its place on the pitcher whichever of
    // them is printed.
    const printedFaces = ref<TPrintedFaces>('front');
    // DC1-06: the customer said no to taking the front again on a blank back; asked anew each
    // time both faces are chosen.
    const isFrontCopyDeclined = ref(false);
    const hasTwoFaces = computed(() => reference.value?.hasVerso === true && printedFaces.value === 'both');
    // Whether the back is printed, alone or with the front: it is then the verso that is edited.
    const hasBackFace = computed(() => reference.value?.hasVerso === true && printedFaces.value !== 'front');

    // The option ticked counts only on a style that can carry it: a move to screen printing leaves
    // the box ticked for a way back, and takes it off the price.
    const canPersonaliseByName = computed(() => BY_NAME_TECHNIQUES.includes(technique.value));
    const hasNominativeOption = computed(() => isNominativeChosen.value && canPersonaliseByName.value);

    const unitPriceExclVat = computed(() => {
        if (!reference.value || quantity.value <= 0) {
            return 0;
        }
        return estimateUnitPriceExclVatInEur({
            family: reference.value.family,
            hasTwoFaces: hasTwoFaces.value,
            isExpress: isExpress.value,
            isNominative: hasNominativeOption.value,
            quantity: quantity.value,
            technique: technique.value,
        });
    });
    const unitPriceInclVat = computed(() => toInclVat(unitPriceExclVat.value));
    const visualCheckInclVat = computed(() => (hasVisualCheck.value ? VISUAL_CHECK_PRICE_INCL_VAT_IN_EUR : 0));
    const totalInclVat = computed(() => Math.round((unitPriceInclVat.value * quantity.value + visualCheckInclVat.value) * 100) / 100);
    const leadTimeInWorkingDays = computed(() =>
        isExpress.value
            ? { max: EXPRESS_LEAD_TIME_IN_WORKING_DAYS, min: EXPRESS_LEAD_TIME_IN_WORKING_DAYS }
            : estimateLeadTimeInWorkingDays(technique.value),
    );

    const isReferenceChosen = computed(() => reference.value !== undefined && cupColour.value !== undefined);

    // Nothing is screen-printed until the customer says so: the technique the store opens on is
    // there for the price. The phone never showed the difference, its scene opening only once the
    // style is chosen; the desktop shows its scene from the start, and painted it in one ink.
    const inkCount = computed(() => {
        const isChosen = isStyleChosen.value;
        const chosen = technique.value;
        if (!isChosen || !SCREEN_PRINTED.includes(chosen)) {
            return 0;
        }
        return chosen === 'serigraphy2' ? 2 : 1;
    });

    // One colour for monocouleur, two for bicolore. Monocouleur names no second colour on
    // purpose: what it does not print is a hole through which the cup shows, not a colour, and
    // the engine paints a hole rather than a white. Quadri prints everything, so no treatment.
    const inkTreatment = computed<TInkTreatment | undefined>(() => {
        if (inkCount.value === 0) {
            return undefined;
        }
        return {
            colours: inkColours.value.slice(0, inkCount.value),
            threshold: inkThreshold.value,
            whiteDistance: whiteDistance.value,
        };
    });

    // A shape is a flat drawing, so it is laid in one colour rather than split across the inks
    // the way a photograph is: the customer says which, per field (Katia, 2026-09-22). Like the
    // treatment it rides on, it is a rendering instruction and is not serialised, so a reload
    // sends these back to the press's own colours.
    const fieldColours = ref<Record<string, string>>({});
    // Which way round the two inks fall on one image. The threshold says where the split is, this
    // says which side of it takes which ink, and it belongs to the image rather than to the press.
    const fieldInkSwaps = ref<Record<string, boolean>>({});

    function setBackgroundPattern(side: TSide, choice: TBackgroundPatternChoice | undefined) {
        const next = { ...backgroundPatterns.value };
        if (choice) {
            next[side] = choice;
        } else {
            delete next[side];
        }
        backgroundPatterns.value = next;
    }

    function setFieldColour(fieldId: string, colour: string) {
        fieldColours.value = { ...fieldColours.value, [fieldId]: colour };
    }

    function toggleFieldInkSwap(fieldId: string) {
        fieldInkSwaps.value = { ...fieldInkSwaps.value, [fieldId]: !fieldInkSwaps.value[fieldId] };
    }

    // What a colour picker may offer for anything printed on the cup. Screen printing lays the
    // inks that were chosen and nothing else, so one ink leaves no choice at all and two leave
    // exactly two; quadri prints any colour, and answers undefined (Katia, 2026-09-22).
    const contentColours = computed<string[] | undefined>(() =>
        inkCount.value === 0 ? undefined : inkColours.value.slice(0, inkCount.value),
    );

    // Choosing a model resets what depended on the previous one, so the customer never
    // keeps a colour or a technique the new cup does not offer.
    function selectReference(name: string) {
        const next = getCatalogReference(name);
        const isNewCup = name !== referenceName.value;
        referenceName.value = name;
        // A new cup restarts the creation: what was laid on the previous one does not follow.
        backgroundPatterns.value = {};
        nominative.value = undefined;
        nominativeFieldIds.value = [];
        isNominativeChosen.value = false;
        if (!next) {
            return;
        }
        // Another cup opens on its own default colour, frosted, even when it comes in the colour
        // chosen for the previous one (client's review, 2026-10-12): the colour was a choice made
        // for that cup, and carried over it read as a choice the customer had not made. Tapping
        // the chosen cup again keeps its colour.
        if (isNewCup || !cupColour.value || !next.colours.includes(cupColour.value)) {
            cupColour.value = defaultColourOf(next);
        }
        const nextTechniques = availableTechniques(next);
        if (!nextTechniques.includes(technique.value)) {
            technique.value = nextTechniques[0];
        }
        if (!next.hasVerso) {
            printedFaces.value = 'front';
        }
        if (!availablePlacements(next, technique.value).includes(quadriPlacement.value)) {
            quadriPlacement.value = 'centre';
        }
        if (quantity.value === 0 || quantity.value % next.cartonSize !== 0) {
            quantity.value = next.cartonSize * DEFAULT_CARTONS;
        }
    }

    // Step 2: the customer says how they will create. Either answer brings its own step 3 in
    // (the print styles, or the themes and designs) and opens it, and that step is still to
    // be answered.
    function chooseCreationMode(mode: TCreationMode) {
        creationMode.value = mode;
        isCreationModeChosen.value = true;
        isStyleChosen.value = false;
        appliedTemplateModelId.value = undefined;
        openSection.value = mode === 'template' ? 'template' : 'style';
    }

    // Step 3 on the "from scratch" path: a print style. It is only offered once that path is
    // chosen, so it never has to switch the mode itself.
    function selectTechnique(next: TPrintTechnique) {
        technique.value = next;
        isStyleChosen.value = true;
        appliedTemplateModelId.value = undefined;
        if (!availablePlacements(reference.value, next).includes(quadriPlacement.value)) {
            quadriPlacement.value = 'centre';
        }
    }

    function declineFrontCopy() {
        isFrontCopyDeclined.value = true;
    }

    function selectPrintedFaces(next: TPrintedFaces) {
        printedFaces.value = next;
        isFrontCopyDeclined.value = false;
    }

    // Step 3 on the "from a design" path: the design decides how the cup is printed, so
    // choosing one closes that step just as a style tile does on the other path.
    function applyTemplateModel(id: string) {
        creationMode.value = 'template';
        appliedTemplateModelId.value = id;
        isStyleChosen.value = true;
    }

    function selectTemplateCategory(categoryId: string, subCategory?: string) {
        templateCategoryId.value = categoryId;
        templateSubCategory.value = subCategory;
        isWholeThemeBrowsed.value = false;
    }

    // Bicolore only: which ink takes the dark end and which the light one is a choice the
    // customer makes by looking at the cup, not by reasoning about luminance.
    function swapInks() {
        const [dark, light, ...rest] = inkColours.value;
        inkColours.value = [light, dark, ...rest];
    }

    function browseWholeTheme() {
        templateSubCategory.value = undefined;
        isWholeThemeBrowsed.value = true;
    }

    return {
        appliedTemplateModelId,
        graduationColour,
        shapeColour,
        graduationStyleId,
        applyTemplateModel,
        backgroundPatterns,
        browseWholeTheme,
        chooseCreationMode,
        contentColours,
        creationMode,
        cupColour,
        fieldColours,
        fieldInkSwaps,
        hasBackFace,
        hasTwoFaces,
        isCreationModeChosen,
        isMobileEditorOpen,
        isMobileStepsDone,
        isMobileStepsOpen,
        inkColours,
        inkCount,
        inkThreshold,
        inkTreatment,
        isReferenceChosen,
        isStyleChosen,
        isWholeThemeBrowsed,
        leadTimeInWorkingDays,
        nominative,
        nominativeFieldIds,
        canPersonaliseByName,
        hasNominativeOption,
        hasVisualCheck,
        isExpress,
        isNominativeChosen,
        visualCheckInclVat,
        openSection,
        pendingChange,
        pendingReferenceName,
        placements,
        printedFaces,
        isFrontCopyDeclined,
        quadriPlacement,
        quantity,
        reference,
        referenceName,
        selectReference,
        declineFrontCopy,
        selectPrintedFaces,
        selectTechnique,
        setBackgroundPattern,
        selectTemplateCategory,
        setFieldColour,
        swapInks,
        technique,
        toggleFieldInkSwap,
        techniques,
        templateCategoryId,
        templateSubCategory,
        totalInclVat,
        unitPriceExclVat,
        unitPriceInclVat,
        whiteDistance,
    };
});

export { useOrderStore };

export type { TBackgroundPatternChoice, TCreationMode, TNominativeFile, TPendingChange, TPrintedFaces, TRailSection };
